/**
 * Routes marketing localisées (FR par défaut, locales sous /{code}/*).
 * Feature flags prod : REACT_APP_LOCALE_ROUTES_{ES,EN,DE,IT}=true
 * (CRA injecte REACT_APP_* au build — pas VITE_*).
 *
 * Hreflang : codes langue ISO (`fr`/`en`/`es`/`de`/`it`) + `x-default` → FR.
 * Pas de variantes région (es-ES, en-GB) dans les balises pour éviter des
 * clusters incomplets. Ciblage pays via contenu localisé + Search Console.
 */

export const MARKETING_LOCALES = ['fr', 'es', 'en', 'de', 'it'] as const;
export type MarketingLocale = (typeof MARKETING_LOCALES)[number];

export const LOCALE_ROUTE_PREFIX: Record<MarketingLocale, string> = {
  fr: '',
  es: '/es',
  en: '/en',
  de: '/de',
  it: '/it',
};

/** Codes hreflang / xhtml:link (alignés booking + SEOHead). */
export const HREFLANG_CODE: Record<MarketingLocale, string> = {
  fr: 'fr',
  en: 'en',
  es: 'es',
  de: 'de',
  it: 'it',
};

export const OG_LOCALE: Record<MarketingLocale, string> = {
  fr: 'fr_FR',
  en: 'en_GB',
  es: 'es_ES',
  de: 'de_DE',
  it: 'it_IT',
};

export type MetierPageId =
  | 'educateurCanin'
  | 'promeneurCanin'
  | 'petSitter'
  | 'pensionCanine'
  | 'comportementaliste'
  | 'toiletteur'
  | 'loisirCanin';

export const METIER_PAGE_IDS: MetierPageId[] = [
  'educateurCanin',
  'promeneurCanin',
  'petSitter',
  'pensionCanine',
  'comportementaliste',
  'toiletteur',
  'loisirCanin',
];

export type AlternateMarketingPaths = Record<MarketingLocale, string>;

/** Chemins marketing par locale (slugs localisés). */
export const MARKETING_PATHS: Record<'home' | MetierPageId, AlternateMarketingPaths> = {
  home: { fr: '/', es: '/es', en: '/en', de: '/de', it: '/it' },
  educateurCanin: {
    fr: '/educateur-canin',
    es: '/es/educador-canino',
    en: '/en/dog-trainer',
    de: '/de/hundetrainer',
    it: '/it/addestratore-cani',
  },
  promeneurCanin: {
    fr: '/promeneur-canin',
    es: '/es/paseador-perros',
    en: '/en/dog-walker',
    de: '/de/hundeausfuehrer',
    it: '/it/passeggiatore-cani',
  },
  petSitter: {
    fr: '/pet-sitter',
    es: '/es/cuidador-perros',
    en: '/en/pet-sitter',
    de: '/de/tiersitter',
    it: '/it/pet-sitter',
  },
  pensionCanine: {
    fr: '/pension-canine',
    es: '/es/residencia-canina',
    en: '/en/dog-boarding',
    de: '/de/hundepension',
    it: '/it/pensione-cani',
  },
  comportementaliste: {
    fr: '/comportementaliste',
    es: '/es/etologo-canino',
    en: '/en/dog-behaviorist',
    de: '/de/verhaltensberater',
    it: '/it/etologo-cani',
  },
  toiletteur: {
    fr: '/toiletteur',
    es: '/es/peluqueria-canina',
    en: '/en/dog-groomer',
    de: '/de/hundefriseur',
    it: '/it/toelettatore-cani',
  },
  loisirCanin: {
    fr: '/loisir-canin',
    es: '/loisir-canin',
    en: '/loisir-canin',
    de: '/loisir-canin',
    it: '/loisir-canin',
  },
};

const PREFIXED_LOCALES: { prefix: string; locale: MarketingLocale }[] = [
  { prefix: '/en', locale: 'en' },
  { prefix: '/es', locale: 'es' },
  { prefix: '/de', locale: 'de' },
  { prefix: '/it', locale: 'it' },
];

function isLocaleRoutesFlagEnabled(envKey: string): boolean {
  const value = process.env[envKey];
  if (value === 'false') return false;
  if (value === 'true') return true;
  // Staging/prod sans flag Netlify UI (ex. previews) : routes SEO actives (#1056)
  return process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'production';
}

export function isLocaleRoutesEsEnabled(): boolean {
  return isLocaleRoutesFlagEnabled('REACT_APP_LOCALE_ROUTES_ES');
}

export function isLocaleRoutesEnEnabled(): boolean {
  return isLocaleRoutesFlagEnabled('REACT_APP_LOCALE_ROUTES_EN');
}

export function isLocaleRoutesDeEnabled(): boolean {
  return isLocaleRoutesFlagEnabled('REACT_APP_LOCALE_ROUTES_DE');
}

export function isLocaleRoutesItEnabled(): boolean {
  return isLocaleRoutesFlagEnabled('REACT_APP_LOCALE_ROUTES_IT');
}

export function isLocaleRouteEnabled(locale: MarketingLocale): boolean {
  if (locale === 'fr') return true;
  if (locale === 'es') return isLocaleRoutesEsEnabled();
  if (locale === 'en') return isLocaleRoutesEnEnabled();
  if (locale === 'de') return isLocaleRoutesDeEnabled();
  if (locale === 'it') return isLocaleRoutesItEnabled();
  return false;
}

const SITE_BASE_URL = 'https://www.rintintin-pro.com';

/** Locales marketing actuellement exposées (FR toujours inclus). */
export function enabledMarketingLocales(): MarketingLocale[] {
  return MARKETING_LOCALES.filter((locale) => isLocaleRouteEnabled(locale));
}

/**
 * Alternates hreflang pour une page MARKETING_PATHS (locales actives seulement).
 * Inclut toujours `fr` + les locales flaggées.
 */
export function marketingHreflangAlternates(
  pageKey: keyof typeof MARKETING_PATHS
): { hrefLang: string; href: string }[] {
  const paths = MARKETING_PATHS[pageKey];
  const locales = enabledMarketingLocales();
  const alternates = locales.map((locale) => ({
    hrefLang: HREFLANG_CODE[locale],
    href: `${SITE_BASE_URL}${paths[locale]}`,
  }));
  alternates.push({
    hrefLang: 'x-default',
    href: `${SITE_BASE_URL}${paths.fr}`,
  });
  return alternates;
}

/** Netlify force souvent un trailing slash — normaliser avant match exact. */
export function normalizeMarketingPathname(pathname: string): string {
  if (!pathname) return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.replace(/\/+$/, '') || '/';
  }
  return pathname;
}

export function getLocaleFromPath(pathname: string): MarketingLocale | null {
  const path = normalizeMarketingPathname(pathname);
  for (const { prefix, locale } of PREFIXED_LOCALES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return locale;
  }
  return null;
}

export function stripLocalePrefix(pathname: string): string {
  const path = normalizeMarketingPathname(pathname);
  for (const { prefix } of PREFIXED_LOCALES) {
    if (path === prefix) return '/';
    if (path.startsWith(`${prefix}/`)) return path.slice(prefix.length) || '/';
  }
  return path;
}

function allPathsForPage(pageKey: keyof typeof MARKETING_PATHS): string[] {
  return Object.values(MARKETING_PATHS[pageKey]);
}

const LOCALE_HOME_PATHS = new Set(
  MARKETING_LOCALES.filter((l) => l !== 'fr').map((l) => LOCALE_ROUTE_PREFIX[l])
);

export function isMarketingPath(pathname: string): boolean {
  const path = normalizeMarketingPathname(pathname);
  const stripped = stripLocalePrefix(path);
  if (stripped === '/' || stripped === '/v2') return true;
  if (LOCALE_HOME_PATHS.has(path)) return true;
  if (isBlogPath(path)) return true;
  return METIER_PAGE_IDS.some((id) => allPathsForPage(id).includes(path));
}

function findPageKey(pathname: string): keyof typeof MARKETING_PATHS | null {
  const path = normalizeMarketingPathname(pathname);
  for (const [key, paths] of Object.entries(MARKETING_PATHS)) {
    if (Object.values(paths).includes(path)) {
      return key as keyof typeof MARKETING_PATHS;
    }
  }
  const stripped = stripLocalePrefix(path);
  for (const [key, paths] of Object.entries(MARKETING_PATHS)) {
    if (paths.fr === stripped) return key as keyof typeof MARKETING_PATHS;
  }
  return null;
}

export function localizePath(pathname: string, locale: MarketingLocale): string {
  const path = normalizeMarketingPathname(pathname);
  const pageKey = findPageKey(path);
  if (pageKey) {
    return MARKETING_PATHS[pageKey][locale];
  }
  const prefix = LOCALE_ROUTE_PREFIX[locale];
  if (locale !== 'fr' && !path.startsWith(prefix)) {
    if (path === '/') return prefix || '/';
    return `${prefix}${path}`;
  }
  if (locale === 'fr') {
    return stripLocalePrefix(path);
  }
  return path;
}

export function getAlternatePaths(pathname: string): AlternateMarketingPaths | null {
  const pageKey = findPageKey(pathname);
  if (!pageKey) return null;
  return MARKETING_PATHS[pageKey];
}

export function getMetierIdFromPath(pathname: string): MetierPageId | null {
  const path = normalizeMarketingPathname(pathname);
  for (const id of METIER_PAGE_IDS) {
    if (allPathsForPage(id).includes(path)) return id;
  }
  const stripped = stripLocalePrefix(path);
  for (const id of METIER_PAGE_IDS) {
    if (MARKETING_PATHS[id].fr === stripped) return id;
  }
  return null;
}

export function getCanonicalPathForLocale(
  pageKey: keyof typeof MARKETING_PATHS,
  locale: MarketingLocale
): string {
  return MARKETING_PATHS[pageKey][locale];
}

/** Map i18n language code → locale marketing pour navigation URL. */
export function marketingLocaleFromLanguage(lang: string): MarketingLocale {
  if (lang === 'es' || lang === 'en' || lang === 'de' || lang === 'it') return lang;
  return 'fr';
}

/** Chemin liste blog par locale (ex. /es/blog). */
export function blogBasePath(locale: MarketingLocale): string {
  const prefix = LOCALE_ROUTE_PREFIX[locale];
  return prefix ? `${prefix}/blog` : '/blog';
}

export function blogArticlePath(locale: MarketingLocale, slug: string): string {
  return `${blogBasePath(locale)}/${slug}`;
}

export function blogCategoryPath(locale: MarketingLocale, categorySlug: string): string {
  return `${blogBasePath(locale)}/category/${categorySlug}`;
}

export function isBlogPath(pathname: string): boolean {
  const stripped = stripLocalePrefix(pathname);
  return stripped === '/blog' || stripped.startsWith('/blog/');
}
